import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormHelperText,
} from '@material-ui/core';
import { uuidv4 } from '../../../../../redux/helpers/helpers';
import { DataGrid } from '@mui/x-data-grid';
import { Close, Person, DeleteOutlineTwoTone } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { register } from '../../../../../redux/services';
import {
  userPrivilegesSelector,
  organizationListSelector,
  privilegesRolesSelector,
  selectedOrganizationSelector,
} from '../../../../../redux/selector';
import { useToast, useUpdateUIState } from '../../../../../redux/action';
import {
  Container,
  Header,
  Content,
  SaveBtn,
  Title,
  Group,
  UserRoleContainer,
} from '../EditUserDrawer/EditUserDrawer.styled';
import { Title as ContentTitle } from '../../GeneralInfo/GeneralInfo.styled';
import {
  allowOnlyNumericValues,
  isEmpty,
  isEmailValid,
  isValidPhoneNumber,
  getEmailErrorHelperText,
  getPhoneNumberErrorHelperText,
  reverseObject,
} from '../../utils';
import TextCopy from '../Shared/TextCopy';

const ROLES = [
  //All roles in order from high to low
  'Super Admin',
  'Super User',
  'In House Testing',
  'Executive Admin',
  'Staff Admin',
  'Staff Editor',
  'Staff Support',
  // Publisher is gated explicitly in roleAccess (Superuser and above only),
  // so its position here does not feed the positional slice rules below.
  'Publisher',
];

const AddUserDrawer = ({ isAddUserDrawerVisible, onClose, updateUserList }) => {
  const userPrivileges = useSelector(userPrivilegesSelector);
  const privileges = useSelector(privilegesRolesSelector);
  const reversedPrivileges = reverseObject(privileges);
  const organizationList = useSelector(organizationListSelector);
  const selectedOrg = useSelector(selectedOrganizationSelector);
  const rolesReversed = [...ROLES].reverse();
  const { fullLoader } = useUpdateUIState();
  const { errorToast, successToast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userRoleOrganization, setUserRoleOrganization] = useState([]);
  const [orgList, setOrgList] = useState({});
  const [selectedOrgID, setSelectedOrgID] = useState('');
  const [multiOrgUser, setMultiOrgUser] = useState(false);
  const [isSaveBtnClicked, setSaveBtnClicked] = useState(false);
  const [isAddBtnClicked, setAddBtnClicked] = useState(false);

  const convertUserRoleToUserRoleID = userRole => {
    return Object.keys(privileges).find(key => privileges[key] === userRole);
  };

  const handleDeleteUserOrg = orgId => {
    setUserRole('');
    const userOrgList = userRoleOrganization.filter(org => org.orgId !== orgId);
    setUserRoleOrganization(userOrgList);
  };

  const loadOrgList = () => {
    if (organizationList) {
      let orgMapping = {};

      for (const org of organizationList) {
        orgMapping[org.id] = org.organizationName;
      }

      setOrgList(orgMapping);
    }
  };

  useEffect(() => {
    loadOrgList();
  }, []);

  // In a Publisher-only org, Publisher is the only and default user type.
  const isPublisherOrg = selectedOrg?.publisher === true;
  useEffect(() => {
    if (isPublisherOrg) setUserRole('Publisher');
  }, [isPublisherOrg]);

  const roleAccess = role => {
    const { isSDA, isSA, isSU, manageUser_Exec, manageUser_Admin } =
      userPrivileges;

    // Publisher role is visible/assignable only to Superuser and above —
    // checked before the positional rules so it never leaks to Exec and below.
    // isSU cascades up to Super Admin / Super Duper Admin, so it alone suffices.
    if (role === 'Publisher') return Boolean(isSU);

    if (isSDA) return true; // Super Duper Admin can do anything

    const userRoles = [
      { privilege: isSA, allowedRoles: ROLES },
      { privilege: isSU, allowedRoles: ROLES.slice(1) },
      { privilege: manageUser_Exec, allowedRoles: ROLES.slice(3) },
      { privilege: manageUser_Admin, allowedRoles: ROLES.slice(4) },
    ];

    return userRoles.some(
      ({ privilege, allowedRoles }) => privilege && allowedRoles.includes(role)
    );
  };

  const pushUserOrganization = () => {
    setAddBtnClicked(true);
    if (!selectedOrgID || !userRole) {
      errorToast(
        'Please select both an organization and a corresponding user role.'
      );
      return;
    }
    const newUserRoleOrg = {
      orgId: selectedOrgID,
      roles: [convertUserRoleToUserRoleID(userRole)],
    };
    const newOrganizations = [...userRoleOrganization, newUserRoleOrg];

    setUserRoleOrganization(newOrganizations);
    setUserRole('');
    setSelectedOrgID('');
  };

  const generateMemberData = () => {
    const date = new Date().toString();

    const newUserData = {
      firstName,
      lastName,
      phoneNumber,
      email,
      delete: false,
      type: 'Individual',
      createdDate: date.toString(),
      updatedDate: date.toString(),
      enable: 1,
      organizationId: selectedOrg.id,
      organizations: userRoleOrganization?.length
        ? userRoleOrganization
        : [
            {
              orgId: selectedOrg.id,
              roles: [convertUserRoleToUserRoleID(userRole)],
            },
          ],
      multiOrgUser,
      authId: uuidv4(),
      id: uuidv4(),
    };

    return newUserData;
  };

  const checkIfRequiredFieldsAreMissing = () => {
    const missingRequiredFields = [];

    if (!firstName) {
      missingRequiredFields.push('First Name');
    }
    if (!lastName) {
      missingRequiredFields.push('Last Name');
    }
    if (!email) {
      missingRequiredFields.push('Email');
    }
    if (!userRoleOrganization.length && !userRole) {
      missingRequiredFields.push('User Role');
    }
    if (!missingRequiredFields.length) return false;

    errorToast(
      `Please fill out all the required fields: ${missingRequiredFields.join(
        ', '
      )}`
    );

    return true;
  };

  const resetFields = () => {
    setUserRole('');
    setSelectedOrgID('');
  };

  const checkIfFieldsAreValid = () => {
    if (!isEmailValid(email)) {
      errorToast('Email is not valid.');
      return false;
    }

    if (!isValidPhoneNumber(phoneNumber, true)) {
      errorToast('Phone number is not valid.');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setSaveBtnClicked(true);
    if (checkIfRequiredFieldsAreMissing()) return;
    if (!checkIfFieldsAreValid()) return;

    fullLoader(true, 'Adding a new user...');

    const userData = generateMemberData();

    const didAddNewUser = await register(userData);

    if (didAddNewUser) {
      await updateUserList();
      successToast('A new user added successfully.');
    } else {
      errorToast('Error adding a new user.');
    }

    onClose();
    fullLoader(false);
    resetFields();
  };

  const columns = [
    { field: 'organization', headerName: 'Organization', flex: 1 },
    { field: 'role', headerName: 'Role', flex: 1 },
    {
      field: 'delete',
      headerName: 'Delete',
      flex: 0.5,
      renderCell: ({ id }) => (
        <DeleteOutlineTwoTone
          onClick={() => handleDeleteUserOrg(id)}
          style={{ fontSize: '18px', cursor: 'pointer' }}
        />
      ),
    },
  ];

  const rows = userRoleOrganization?.map(({ orgId, roles }) => {
    return {
      id: orgId,
      organization: orgList[orgId],
      role: roles
        ?.map(roleId =>
          privileges[roleId] !== 'SUPER_USER'
            ? privileges[roleId]
            : 'Super User'
        )
        .join(', '),
    };
  });

  const getUserSelect = () => (
    <FormControl fullWidth variant="outlined" size="small">
      <InputLabel htmlFor="role">User Role*</InputLabel>
      <Select
        label="Role"
        name="role"
        value={userRole}
        onChange={event => {
          setUserRole(event.target.value);
        }}
        error={
          isSaveBtnClicked && isEmpty(userRole) && !userRoleOrganization.length
        }
      >
        {privileges &&
          (isPublisherOrg ? ['Publisher'] : rolesReversed).map(role =>
            roleAccess(role) ? (
              <MenuItem key={reversedPrivileges[role]} value={role}>
                {role}
              </MenuItem>
            ) : null
          )}
      </Select>
      {isSaveBtnClicked &&
        isEmpty(userRole) &&
        !userRoleOrganization.length && (
          <FormHelperText style={{ color: '#f44335' }}>
            Required field
          </FormHelperText>
        )}
    </FormControl>
  );

  return (
    <Drawer anchor={'right'} onClose={onClose} open={isAddUserDrawerVisible}>
      <Container>
        <Header>
          <Group>
            <IconButton onClick={onClose} style={{ color: 'white' }}>
              <Close />
            </IconButton>
            <Title>Add a new user</Title>
          </Group>
          <SaveBtn onClick={handleSubmit}>Save</SaveBtn>
        </Header>
        <Content>
          <ContentTitle>
            <Person size="small" />
            User Details
          </ContentTitle>
          <TextField
            label="First Name*"
            variant="outlined"
            size="small"
            value={firstName}
            error={isSaveBtnClicked && isEmpty(firstName)}
            helperText={
              isSaveBtnClicked && isEmpty(firstName) ? 'Required field' : ''
            }
            onChange={e => setFirstName(e.target.value)}
          />
          <TextField
            label="Last Name*"
            variant="outlined"
            size="small"
            value={lastName}
            error={isSaveBtnClicked && isEmpty(lastName)}
            helperText={
              isSaveBtnClicked && isEmpty(lastName) ? 'Required field' : ''
            }
            onChange={e => setLastName(e.target.value)}
          />
          <TextField
            label="Email*"
            variant="outlined"
            size="small"
            value={email}
            error={isSaveBtnClicked && (isEmpty(email) || !isEmailValid(email))}
            helperText={isSaveBtnClicked && getEmailErrorHelperText(email)}
            onChange={e => setEmail(e.target.value)}
          />
          <TextField
            label="Phone"
            variant="outlined"
            size="small"
            value={phoneNumber}
            error={isSaveBtnClicked && !isValidPhoneNumber(phoneNumber, true)}
            helperText={
              isSaveBtnClicked &&
              getPhoneNumberErrorHelperText(phoneNumber, true)
            }
            onChange={e =>
              setPhoneNumber(allowOnlyNumericValues(e.target.value, 10))
            }
          />
          {/* Only for not super admin users */}
          {userPrivileges.manageUser_Admin &&
            !userPrivileges.isSU &&
            !userPrivileges.manageAuthOrgs &&
            getUserSelect()}
          {/* Only for executive admins and up; not in publisher-only orgs */}
          {userPrivileges.manageUser_Exec && !isPublisherOrg && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={multiOrgUser}
                  onChange={event => {
                    setMultiOrgUser(event.target.checked);
                  }}
                  color="primary"
                />
              }
              label="Multi-Organization User"
              style={{ width: '250px' }}
            />
          )}
          {/* Only for super users and up users */}
          {(userPrivileges.isSU || userPrivileges.manageAuthOrgs) && (
            <UserRoleContainer>
              {multiOrgUser ? (
                <>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel htmlFor="organization">
                      Organization*
                    </InputLabel>
                    <Select
                      label="Organization"
                      name="organization"
                      value={selectedOrgID}
                      onChange={event => {
                        setSelectedOrgID(event.target.value);
                      }}
                      error={
                        (isSaveBtnClicked || isAddBtnClicked) &&
                        isEmpty(selectedOrgID) &&
                        !userRoleOrganization.length
                      }
                    >
                      {orgList &&
                        Object.entries(orgList).map(([orgId, org]) => (
                          <MenuItem key={orgId} value={orgId}>
                            {org}
                          </MenuItem>
                        ))}
                    </Select>
                    {(isSaveBtnClicked || isAddBtnClicked) &&
                      isEmpty(selectedOrgID) &&
                      !userRoleOrganization.length && (
                        <FormHelperText style={{ color: '#f44335' }}>
                          Required field
                        </FormHelperText>
                      )}
                  </FormControl>
                  {getUserSelect()}
                  <Button
                    variant="outlined"
                    style={{ height: '40px' }}
                    onClick={pushUserOrganization}
                  >
                    Add
                  </Button>
                </>
              ) : (
                getUserSelect()
              )}
            </UserRoleContainer>
          )}
          {multiOrgUser && userRoleOrganization.length > 0 && (
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={5}
              disableSelectionOnClick
              autoHeight
            />
          )}
          <TextCopy />
        </Content>
      </Container>
    </Drawer>
  );
};

export default AddUserDrawer;
